import React from "react";
import { useNavigate } from "react-router-dom";
import { createObject, sectionDataObject } from "@/lib/stores/objectsStore";
import { createTemplate } from "@/lib/stores/templatesStore";
import { createDocumentItem } from "@/lib/stores/documentStore";

interface UseFolderAwareHandlersProps {
  itemType: "blueprints" | "documents" | "library" | "dialogs" | "flows" | "variables";
  selectedFolderId: string | null;
  selectedGame: any;
  data: any[];
  toast: any;
  
  // Original handlers from useUnifiedItemsPage
  originalHandleGlossaryItemCreated?: (item: any) => Promise<void>;
  originalHandleDocumentCreated?: (documentId: string) => void;
  originalHandleSaveBlueprint?: (item: any) => Promise<void>;
  
  // Wizard state setters
  setIsAddWizardOpen: (open: boolean) => void;
  setIsCreateWizardOpen: (open: boolean) => void;
  setIsEditModalOpen: (open: boolean) => void;
  setIsDialogWizardOpen: (open: boolean) => void;
  setSelectedBlueprint: (blueprint: any) => void;
  setSelectedGlossaryItem: (item: any) => void;
  setSelectedDialog: (dialog: any) => void;
  
  // Dialog context methods
  createDialogItem?: (item: any) => Promise<any>;
  updateDialogItem?: (item: any) => Promise<any>;
}

/**
 * Hook to create folder-aware handlers that inject folder assignment into item creation
 * Wraps original handlers from useUnifiedItemsPage to add folder context
 */
export function useFolderAwareHandlers({
  itemType,
  selectedFolderId,
  selectedGame,
  data,
  toast,
  originalHandleGlossaryItemCreated,
  originalHandleDocumentCreated,
  originalHandleSaveBlueprint,
  setIsAddWizardOpen,
  setIsCreateWizardOpen,
  setIsEditModalOpen,
  setIsDialogWizardOpen,
  setSelectedBlueprint,
  setSelectedGlossaryItem,
  setSelectedDialog,
  createDialogItem,
  updateDialogItem,
}: UseFolderAwareHandlersProps) {
  const navigate = useNavigate();

  // Glossary item creation handler with folder assignment
  const handleGlossaryItemCreated = React.useCallback(async (newItem: {
    id?: string;
    game_id?: string;
    name: string;
    tags: string[];
    data: sectionDataObject[];
    blueprint_id?: string | null;
    version?: {
      changes: Record<string, unknown>;
      version: number;
    };
    blueprint_version?: number;
    identifiers?: string[];
  }) => {
    try {
      if (!selectedGame?.id) {
        toast({
          title: "Error",
          description: "No game selected",
          variant: "destructive",
        });
        return;
      }

      // Prepare the item data for creation with folder assignment
      const itemData = {
        game_id: newItem.game_id || selectedGame.id,
        name: newItem.name,
        type: "glossary",
        tags: newItem.tags || [],
        data: newItem.data,
        blueprint_id: newItem.blueprint_id || null,
        blueprint_version: newItem.blueprint_version || 1,
        identifiers: newItem.identifiers || [],
        folder: selectedFolderId, // Assign to current folder
      };

      // Create the glossary item via the store
      const createdItem = await createObject(itemData, toast);

      // Show success message
      toast({
        title: "Success",
        description: `Glossary item "${createdItem.name}" created successfully`,
      });

      // Close the wizard
      setIsAddWizardOpen(false);
      setIsCreateWizardOpen(false);
      setIsEditModalOpen(false);

      // Clear selected glossary item
      setSelectedGlossaryItem(null);
    } catch (error) {
      console.error("Error creating glossary item:", error);
      // Error handling is already done by the createObject function via toast
    }
  }, [selectedGame, selectedFolderId, toast, setIsAddWizardOpen, setIsCreateWizardOpen, setIsEditModalOpen, setSelectedGlossaryItem]);

  // Document creation handler with folder assignment
  const handleDocumentCreated = React.useCallback(async (newItem: any) => {
    try {
      if (!selectedGame?.id) {
        toast({
          title: "Error",
          description: "No game selected",
          variant: "destructive",
        });
        return;
      }

      // Prepare the item data for creation with folder assignment
      const itemData = {
        ...newItem,
        game_id: newItem.game_id || selectedGame.id,
        folder: selectedFolderId, // Assign to current folder
      };

      // Create the document item via the store
      const createdItem = await createDocumentItem(itemData, toast);

      // Show success message
      toast({
        title: "Success",
        description: `Document "${createdItem.name}" created successfully`,
      });

      // Close the wizard
      setIsCreateWizardOpen(false);
    } catch (error) {
      console.error("Error creating document:", error);
    }
  }, [selectedGame, selectedFolderId, toast, setIsCreateWizardOpen]);

  // Blueprint save handler with folder assignment
  const handleSaveBlueprint = React.useCallback(async (blueprintData: any) => {
    try {
      if (!selectedGame?.id) {
        toast({
          title: "Error",
          description: "No game selected",
          variant: "destructive",
        });
        return;
      }

      // Prepare the blueprint data for creation with folder assignment
      const itemData = {
        ...blueprintData,
        game_id: blueprintData.game_id || selectedGame.id,
        folder: selectedFolderId, // Assign to current folder
      };

      // Create or update the blueprint
      if (blueprintData.id) {
        // This is an update, use the original handler
        if (originalHandleSaveBlueprint) {
          await originalHandleSaveBlueprint(blueprintData);
        }
      } else {
        // This is a new blueprint, create with folder assignment
        const createdBlueprint = await createTemplate(itemData, toast);

        // Show success message
        toast({
          title: "Success",
          description: `Blueprint "${createdBlueprint.name}" created successfully`,
        });

        // Close the wizard
        setIsEditModalOpen(false);
        setSelectedBlueprint(null);
      }
    } catch (error) {
      console.error("Error saving blueprint:", error);
    }
  }, [selectedGame, selectedFolderId, toast, originalHandleSaveBlueprint, setIsEditModalOpen, setSelectedBlueprint]);

  // Dialog save handler with folder assignment
  const handleDialogSave = React.useCallback(async (dialogData: any) => {
    try {
      if (!selectedGame?.id) {
        toast({
          title: "Error",
          description: "No game selected",
          variant: "destructive",
        });
        return;
      }

      // Prepare the dialog data with folder assignment
      const itemData = {
        ...dialogData,
        game_id: dialogData.game_id || selectedGame.id,
        folder: selectedFolderId, // Assign to current folder
      };

      // Create or update the dialog
      if (dialogData.id) {
        // Update existing dialog
        if (updateDialogItem) {
          await updateDialogItem(itemData);
          toast({
            title: "Success",
            description: `Dialog "${dialogData.name}" updated successfully`,
          });
        }
      } else {
        // Create new dialog
        if (createDialogItem) {
          const createdDialog = await createDialogItem(itemData);
          toast({
            title: "Success",
            description: `Dialog "${createdDialog.name}" created successfully`,
          });
        }
      }

      // Close the wizard
      setIsDialogWizardOpen(false);
      setSelectedDialog(null);
    } catch (error) {
      console.error("Error saving dialog:", error);
      // Error handling is done by the store
    }
  }, [selectedGame, selectedFolderId, toast, createDialogItem, updateDialogItem, setIsDialogWizardOpen, setSelectedDialog]);

  // Custom handleAdd function that includes folder assignment
  const handleAddWithFolder = React.useCallback(() => {
    if (itemType === "blueprints") {
      // Navigate to new template page with folder context
      const searchParams = selectedFolderId ? `?folder=${selectedFolderId}` : '';
      navigate(`/templates/new${searchParams}`);
    } else if (itemType === "documents") {
      // Navigate to new document page with folder context (document is only saved when user presses Create)
      const searchParams = selectedFolderId ? `?folder=${selectedFolderId}` : '';
      navigate(`/documents/new${searchParams}`);
    } else if (itemType === "library") {
      // Navigate to new object page with folder context
      const searchParams = selectedFolderId ? `?folder=${selectedFolderId}` : '';
      navigate(`/library/new${searchParams}`);
    } else if (itemType === "dialogs") {
      // Navigate to new dialog page with folder context
      const searchParams = selectedFolderId ? `?folder=${selectedFolderId}` : '';
      navigate(`/dialogs/new${searchParams}`);
    } else if (itemType === "variables") {
      // Navigate to new variable page with folder context (variable is only saved when user presses Save)
      const searchParams = selectedFolderId ? `?folder=${selectedFolderId}` : '';
      navigate(`/variables/new${searchParams}`);
    }
  }, [
    itemType,
    selectedGame,
    selectedFolderId,
    data,
    toast,
    navigate,
    setIsAddWizardOpen,
  ]);

  return {
    handleGlossaryItemCreated,
    handleDocumentCreated,
    handleSaveBlueprint,
    handleDialogSave,
    handleAddWithFolder,
  };
}

